import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Select,
  Text,
  BlockStack,
  InlineStack,
  InlineGrid,
  DataTable,
  SkeletonBodyText,
  Banner,
  Box,
  EmptyState,
} from '@shopify/polaris';
import { useShopifyFetch, AnalyticsOverview, ShopInfo, ConversionData } from '../hooks/useShopifyFetch';

export default function Analytics() {
  const { get } = useShopifyFetch();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('30');

  // Fetch shop info to check connection
  const { data: shop } = useQuery<ShopInfo>({
    queryKey: ['shop'],
    queryFn: () => get('/api/shop'),
  });

  // Fetch analytics
  const {
    data: analytics,
    isLoading,
    error,
  } = useQuery<AnalyticsOverview>({
    queryKey: ['analytics', 'overview', timeRange],
    queryFn: () => get(`/api/analytics/overview?days=${timeRange}`),
    enabled: !!shop?.is_connected,
  });

  // Fetch conversions
  const { data: conversionsData } = useQuery<ConversionData & { conversions?: Array<{ order_id?: string; affiliate_name?: string; product_title?: string; order_value?: number; created_at: string }> }>({
    queryKey: ['conversions', timeRange],
    queryFn: () => get(`/api/analytics/conversions?limit=20`),
    enabled: !!shop?.is_connected,
  });

  const timeRangeOptions = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 90 days', value: '90' },
    { label: 'Last year', value: '365' },
  ];

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportError(null);

    // Map frontend timeRange (days string) to backend period format
    const periodMap: Record<string, string> = {
      '7': '7d',
      '30': '30d',
      '90': '90d',
      '365': '365d',
    };
    const period = periodMap[timeRange] || '30d';

    try {
      const result = await get<{
        success: boolean;
        filename: string;
        content: string;
        content_type: string;
        error?: string;
      }>(`/api/analytics/export?format=csv&period=${period}`);

      if (!result.success) {
        setExportError(result.error || 'Export failed. Please try again.');
        return;
      }

      // Create a Blob from the CSV content and trigger browser download
      const blob = new Blob([result.content], { type: result.content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(
        err instanceof Error ? err.message : 'Export failed. Please try again.'
      );
    } finally {
      setExporting(false);
    }
  }, [get, timeRange]);

  if (!shop?.is_connected) {
    return (
      <Page title="Analytics">
        <Layout>
          <Layout.Section>
            <Banner tone="warning">
              <p>
                Connect your Affilync account in Settings to view affiliate analytics.
              </p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Analytics">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load analytics. Please try again.</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Analytics"
      subtitle="Track affiliate performance and conversions"
      secondaryActions={[
        {
          content: exporting ? 'Exporting…' : 'Export CSV',
          onAction: handleExport,
          loading: exporting,
          disabled: exporting || isLoading,
        },
      ]}
    >
      <Layout>
        {/* Export Error Banner */}
        {exportError && (
          <Layout.Section>
            <Banner
              tone="critical"
              onDismiss={() => setExportError(null)}
            >
              <p>{exportError}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Time Range Filter */}
        <Layout.Section>
          <Card>
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingMd" as="h2">
                Performance Overview
              </Text>
              <Box width="200px">
                <Select
                  label="Time range"
                  labelHidden
                  options={timeRangeOptions}
                  value={timeRange}
                  onChange={setTimeRange}
                />
              </Box>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Stats Cards */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
            <Card>
              <BlockStack gap="200">
                <Text variant="bodySm" tone="subdued" as="p">
                  Total Clicks
                </Text>
                {isLoading ? (
                  <SkeletonBodyText lines={1} />
                ) : (
                  <Text variant="heading2xl" as="p">
                    {(analytics?.clicks || 0).toLocaleString()}
                  </Text>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text variant="bodySm" tone="subdued" as="p">
                  Conversions
                </Text>
                {isLoading ? (
                  <SkeletonBodyText lines={1} />
                ) : (
                  <Text variant="heading2xl" as="p">
                    {(analytics?.conversions || 0).toLocaleString()}
                  </Text>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text variant="bodySm" tone="subdued" as="p">
                  Revenue
                </Text>
                {isLoading ? (
                  <SkeletonBodyText lines={1} />
                ) : (
                  <Text variant="heading2xl" as="p">
                    ${(analytics?.revenue || 0).toLocaleString()}
                  </Text>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text variant="bodySm" tone="subdued" as="p">
                  Conversion Rate
                </Text>
                {isLoading ? (
                  <SkeletonBodyText lines={1} />
                ) : (
                  <Text variant="heading2xl" as="p">
                    {analytics?.clicks
                      ? ((analytics.conversions / analytics.clicks) * 100).toFixed(1)
                      : '0'}
                    %
                  </Text>
                )}
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* Top Affiliates */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Top Affiliates
              </Text>
              {isLoading ? (
                <SkeletonBodyText lines={5} />
              ) : analytics?.top_affiliates && analytics.top_affiliates.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric']}
                  headings={['Affiliate', 'Conversions', 'Revenue']}
                  rows={analytics.top_affiliates.map((affiliate) => [
                    affiliate.name,
                    affiliate.conversions,
                    `$${affiliate.revenue.toLocaleString()}`,
                  ])}
                />
              ) : (
                <Text variant="bodySm" tone="subdued" as="p">
                  No affiliate data yet
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Top Products */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Top Products
              </Text>
              {isLoading ? (
                <SkeletonBodyText lines={5} />
              ) : analytics?.top_products && analytics.top_products.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric']}
                  headings={['Product', 'Conversions', 'Revenue']}
                  rows={analytics.top_products.map((product) => [
                    product.title,
                    product.conversions,
                    `$${product.revenue.toLocaleString()}`,
                  ])}
                />
              ) : (
                <Text variant="bodySm" tone="subdued" as="p">
                  No product data yet
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Recent Conversions */}
        <Layout.Section>
          <Card>
            {conversionsData?.conversions && conversionsData.conversions.length > 0 ? (
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  Recent Conversions
                </Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'numeric', 'text']}
                  headings={['Order', 'Affiliate', 'Product', 'Amount', 'Date']}
                  rows={conversionsData.conversions.map((conv: any) => [
                    conv.order_id || '-',
                    conv.affiliate_name || 'Unknown',
                    conv.product_title || '-',
                    `$${(conv.order_value || 0).toFixed(2)}`,
                    new Date(conv.created_at).toLocaleDateString(),
                  ])}
                />
              </BlockStack>
            ) : (
              <EmptyState
                heading="No conversions yet"
                action={{
                  content: 'Generate affiliate links',
                  onAction: () => navigate(`/links${window.location.search}`),
                }}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>
                  Conversions will appear here when orders come through your affiliate
                  links. Share links to start tracking sales.
                </p>
              </EmptyState>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
